import { useEffect, useMemo, useRef, useState } from 'react'
import sprint, {
  SprintDetail,
  SprintStatusDisplayOrder,
} from '../../../../../lib/functions/sprint'
import { SelectOption } from '../../../../model/selectOption'
import { TeamSprintGroup } from './sprintGroupTypes'

type UseTeamSprintGroupsResult = {
  teamSprintGroups: TeamSprintGroup[]
  sprintOptions: SelectOption[]
}

export const useTeamSprintGroups = (
  teamUuids: string[] | undefined,
  teamOptions: SelectOption[]
): UseTeamSprintGroupsResult => {
  const [sprintOptions, setSprintOptions] = useState<SelectOption[]>([])
  const sprintCacheRef = useRef<Map<string, SprintDetail[]>>(new Map())

  useEffect(() => {
    if (!teamUuids || teamUuids.length === 0) {
      sprintCacheRef.current.clear()
      setSprintOptions([])
      return
    }

    const cache = sprintCacheRef.current
    const currentUuids = new Set(teamUuids)

    // 選択解除されたチームのキャッシュを削除
    for (const key of cache.keys()) {
      if (!currentUuids.has(key)) {
        cache.delete(key)
      }
    }

    // 未取得のチームだけ特定
    const newUuids = teamUuids.filter(uuid => !cache.has(uuid))

    const getSortIndex = (status: string) =>
      SprintStatusDisplayOrder.indexOf(
        status as (typeof SprintStatusDisplayOrder)[number]
      )

    const buildOptions = () =>
      Array.from(cache.values())
        .flat()
        .sort((a, b) => getSortIndex(a.status) - getSortIndex(b.status))
        .map(v => ({ value: v.uuid, name: v.name, groupBy: v.status }))

    if (newUuids.length === 0) {
      // キャッシュのみで再構成（チーム削除時）
      setSprintOptions(buildOptions())
      return
    }

    // 新規チームのみフェッチ
    Promise.all(
      newUuids.map(async uuid => {
        const sprints = await sprint.searchByText(uuid)
        cache.set(uuid, sprints)
      })
    ).then(() => {
      setSprintOptions(buildOptions())
    })
  }, [teamUuids])

  const teamSprintGroups: TeamSprintGroup[] = useMemo(() => {
    if (!teamUuids || teamUuids.length === 0) return []
    const cache = sprintCacheRef.current
    return teamUuids
      .filter(uuid => cache.has(uuid))
      .map(uuid => ({
        teamUuid: uuid,
        teamName: teamOptions.find(t => t.value === uuid)?.name ?? uuid,
        sprints: cache.get(uuid)!,
      }))
    // sprintOptions を依存に含めることで、fetch完了時に再計算される
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamUuids, teamOptions, sprintOptions])

  return { teamSprintGroups, sprintOptions }
}
